/**
 * @file AssetForm.js
 * @description Shared form for adding a new asset (upload from device) or
 * editing an existing asset's metadata. Used by PlanFilesManager.
 */

import { useState, useEffect } from 'react';
import { FileDrop } from '../../helpers/FileDrop';
import { InputField, SwitchField } from '../../helpers/FormComponents';
import TagInput from '../../helpers/TagInput';
import Tooltip from '../../ui/tooltips/Tooltip';
import PopulationGroupMultiSelect from '../../shared/asyncSearchableMultiSelect/PopulationGroupMultiSelect';
import Spinner from '../../ui/spinners/Spinner';
import Toast from '../../ui/toasts/Toast';
import { isValidFileTypeAndSize } from '../../../utils/validate';
import {
    createAsset,
    updateAsset,
    fetchAssetByID,
} from '../../../services/clientService';
import { uploadFile, updateFile } from '../../../services/containerService';
import { useUploadContext } from '../../../store/Contexts';
import {
    formatFileSize,
    sanitizeFileName,
    getFileCategory,
} from '../../helpers/FileUtils';
import { getFileIcon } from '../../ui/svg/FileIcons';
import VideoThumbnail from '../../ui/VideoThumbnail';
import styles from '../PlanYear.module.scss';
import { getText } from '../../../i18n';

const DEFAULT_ASSET = {
    name: '',
    assetTypeId: 2,
    assetTags: [],
    description: '',
    assetGroups: [],
    assetIndividuals: [],
};

const MODE = {
    ADD: 'add',
    EDIT: 'edit',
};

const normalizeAssetTags = rawTags => {
    if (!Array.isArray(rawTags)) {
        return [];
    }

    return rawTags
        .map(tagValue => {
            if (typeof tagValue === 'string') {
                return tagValue.trim();
            }

            if (
                tagValue &&
                typeof tagValue === 'object' &&
                typeof tagValue.tag === 'string'
            ) {
                return tagValue.tag.trim();
            }

            return '';
        })
        .filter(Boolean);
};

const getApiErrorMessage = (error, fallbackMessage) => {
    const apiMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.detail ||
        error?.message;

    return typeof apiMessage === 'string' && apiMessage.trim() !== ''
        ? apiMessage
        : fallbackMessage;
};

const normalizeAssociationIds = rawItems => {
    if (!Array.isArray(rawItems)) {
        return [];
    }

    return rawItems
        .map(item => {
            if (typeof item === 'object' && item !== null) {
                return (
                    item.populationGroupId ||
                    item.individualId ||
                    item.id ||
                    null
                );
            }

            return item;
        })
        .filter(Boolean)
        .map(itemId => itemId.toString());
};

const AssetForm = ({
    mode = MODE.ADD,
    existingAsset = null,
    assetId = null,
    entityLabel,
    showPopulationGroups = false,
    persistPopulationGroupsOnAsset = true,
    showNameField = true,
    showTagsField = true,
    title: titleOverride,
    subtitle: subtitleOverride,
    onSaved,
    onCancel,
}) => {
    const [file, setFile] = useState(null);
    const { enqueueUpload } = useUploadContext();
    const [asset, setAsset] = useState(DEFAULT_ASSET);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoadingAsset, setIsLoadingAsset] = useState(false);

    useEffect(() => {
        const finalAssetId =
            assetId || existingAsset?.id || existingAsset?.assetId;
        if (mode !== MODE.EDIT || !finalAssetId) {
            return;
        }

        const loadAssetDetails = async () => {
            setIsLoadingAsset(true);
            try {
                const fetchedAsset = await fetchAssetByID(
                    finalAssetId,
                    null,
                    false,
                    true,
                );

                setAsset({
                    ...DEFAULT_ASSET,
                    ...fetchedAsset,
                    assetTags: normalizeAssetTags(fetchedAsset.assetTags),
                    assetGroups: persistPopulationGroupsOnAsset
                        ? (fetchedAsset.assetGroups || []).map(group => ({
                              id: group.populationGroupId || group.id,
                          }))
                        : existingAsset?.assetGroups || [],
                });
            } catch (error) {
                console.error('Failed to fetch asset details', error);
                Toast.error(getText('msg_4fc7d202d910'));
            } finally {
                setIsLoadingAsset(false);
            }
        };

        loadAssetDetails();
    }, [mode, existingAsset, assetId, persistPopulationGroupsOnAsset]);

    const updateAssetField = (key, value) => {
        setAsset(prev => ({
            ...prev,
            [key]: value,
        }));
    };

    const handleFileChange = fileValue => {
        setFile(fileValue);

        if (fileValue && (!asset.name || asset.name.trim() === '')) {
            const fileName = fileValue.name.split('.')[0];
            setAsset(prev => ({
                ...prev,
                name: fileName,
            }));
        }
    };

    const validateForm = () => {
        if (mode === MODE.ADD && !file) {
            Toast.error(getText('msg_3bad71815a76'));
            return false;
        }

        if (file) {
            const fileTypeAndSizeValidation = isValidFileTypeAndSize(file);
            if (fileTypeAndSizeValidation) {
                Toast.error(fileTypeAndSizeValidation);
                return false;
            }
        }

        if (!asset.name || asset.name.trim() === '') {
            Toast.error(getText('msg_d7659e2c4af2'));
            return false;
        }

        if (asset.name.length > 256) {
            Toast.error(getText('msg_f4231db74e51'));
            return false;
        }

        return true;
    };

    const buildAssetPayload = urlResponse => {
        const tenantId = +window.localStorage.getItem('tenantId');
        const payload = { ...asset, tenantId };
        payload.assetTags = normalizeAssetTags(payload.assetTags);
        payload.assetIndividuals = normalizeAssociationIds(
            payload.assetIndividuals,
        );

        if (showPopulationGroups && persistPopulationGroupsOnAsset) {
            payload.assetGroups = normalizeAssociationIds(payload.assetGroups);
        }

        if (!persistPopulationGroupsOnAsset) {
            delete payload.assetGroups;
        }

        if (urlResponse) {
            payload.url = urlResponse;
            payload.fileType = file.name.split('.').pop().toLowerCase();
            payload.fileSize = formatFileSize(file.size);
        }

        if (showPopulationGroups && persistPopulationGroupsOnAsset) {
            payload.assetGroups = normalizeAssociationIds(asset.assetGroups);
        }

        if (!payload.description) {
            payload.description = `${payload.name} `;
        }

        return payload;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        setIsSaving(true);
        try {
            let urlResponse = null;

            if (file) {
                const sanitizedFile = sanitizeFileName(file);
                urlResponse = await enqueueUpload(
                    sanitizedFile.name,
                    uploadOptions =>
                        mode === MODE.EDIT
                            ? updateFile(sanitizedFile, uploadOptions)
                            : uploadFile(sanitizedFile, uploadOptions),
                );

                if (!urlResponse) {
                    throw new Error(getText('msg_a2918c10575b'));
                }
            }

            const payload = buildAssetPayload(urlResponse);

            if (mode === MODE.ADD) {
                const createResponse = await createAsset(payload, null);

                if (
                    !createResponse ||
                    createResponse.error ||
                    !createResponse.data ||
                    !createResponse.data.id
                ) {
                    throw new Error(
                        createResponse?.message || getText('msg_0c145b0f212c'),
                    );
                }

                Toast.success(getText('msg_7d5d42a16dc4', { entityLabel }));
                onSaved(
                    {
                        ...createResponse.data,
                        ...(showPopulationGroups
                            ? { assetGroups: asset.assetGroups }
                            : {}),
                    },
                    MODE.ADD,
                );
            } else {
                const updateResponse = await updateAsset(payload, null);

                if (!updateResponse || updateResponse.error) {
                    throw new Error(
                        updateResponse?.message || getText('msg_8b2027f29271'),
                    );
                }

                Toast.success(getText('msg_85874c6ac085'));
                onSaved(
                    {
                        ...(updateResponse.data || payload),
                        ...(showPopulationGroups
                            ? { assetGroups: asset.assetGroups }
                            : {}),
                    },
                    MODE.EDIT,
                );
            }
        } catch (error) {
            console.error('Failed to save asset', error);
            Toast.error(
                getApiErrorMessage(
                    error,
                    mode === MODE.ADD
                        ? getText('msg_5d1135fddcdd')
                        : getText('msg_3a76fbeb5fcc'),
                ),
            );
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        setFile(null);
        setAsset(DEFAULT_ASSET);
        onCancel();
    };

    const heading =
        titleOverride ||
        (mode === MODE.ADD
            ? getText('msg_0625a2d6e606')
            : getText('msg_3f0ec1ac9673'));
    const subtitleText =
        subtitleOverride ||
        (mode === MODE.ADD
            ? getText(
                  showPopulationGroups
                      ? 'msg_70d6e6343e33'
                      : 'msg_4b7d9c1aef02',
              )
            : null);
    const submitLabel =
        mode === MODE.ADD
            ? getText('msg_8bdf057f91e7')
            : getText('msg_efc007a393f6');
    const isEditAssetImage = getFileCategory(asset.fileType) === 'image';
    const isEditAssetVideo = getFileCategory(asset.fileType) === 'video';

    return (
        <div className={`${styles.modalContent} ${styles.assetFormModal}`}>
            <div className={`${styles.assetFormHeader} mb-4`}>
                <h4>{heading}</h4>

                {subtitleText && (
                    <p className={`text-muted ${styles.assetFormHint}`}>
                        {subtitleText}
                    </p>
                )}
            </div>

            {isLoadingAsset && (
                <div className={`text-center my-4 ${styles.assetFormLoading}`}>
                    <Spinner width={50} className={styles.loadingSpinner} />
                </div>
            )}

            {!isLoadingAsset && (
                <>
                    <div className={styles.assetFormBody}>
                        {mode === MODE.ADD && (
                            <FileDrop file={file} onChange={handleFileChange} />
                        )}

                        {mode === MODE.EDIT && (
                            <div className={styles.selectedAssetRow}>
                                <div className={styles.selectedAssetThumb}>
                                    {isEditAssetImage && asset.url ? (
                                        <img
                                            src={asset.url}
                                            alt={
                                                asset.name ||
                                                getText('msg_2c44db006398')
                                            }
                                        />
                                    ) : isEditAssetVideo ? (
                                        <VideoThumbnail
                                            videoUrl={asset.url}
                                            width={80}
                                            height={64}
                                            alt={
                                                asset.name ||
                                                getText('msg_a2fc35af26ea')
                                            }
                                        />
                                    ) : (
                                        getFileIcon(asset.fileType)
                                    )}
                                </div>
                                <div className={styles.selectedAssetMeta}>
                                    <h5 className={styles.selectedAssetLabel}>
                                        {getText('msg_10e7ac23a20a')}
                                    </h5>
                                    <div
                                        className={`${styles.selectedAssetValue} ${styles.selectedAssetValueSpacing}`}
                                    >
                                        {asset.name ||
                                            getText('msg_621521f9a878')}
                                    </div>
                                    <h5
                                        className={`${styles.selectedAssetLabel} mt-4`}
                                    >
                                        {getText('msg_5023c8994cbd')}
                                    </h5>
                                    <div className={styles.selectedAssetValue}>
                                        {asset.fileType?.toUpperCase() ||
                                            getText('msg_08d2e98e6754')}
                                    </div>
                                </div>
                            </div>
                        )}

                        {showNameField && (
                            <InputField
                                label={getText('msg_709a23220f2c')}
                                onChange={value =>
                                    updateAssetField('name', value)
                                }
                                maxChars={256}
                                required={true}
                                placeholder={getText('msg_b7ffb9789f09')}
                                value={asset.name}
                            />
                        )}

                        {showTagsField && (
                            <div className={styles.formSection}>
                                <div className="file-form-label">
                                    {getText('msg_848eed0fbd54')}
                                    <Tooltip
                                        html={getText('msg_79449ece0a91')}
                                        placement="right"
                                    >
                                        <i
                                            className={`bi bi-info-circle ${styles.readOnlyInfoIcon}`}
                                        ></i>
                                    </Tooltip>
                                </div>
                                <TagInput
                                    onTagsChange={tags =>
                                        updateAssetField('assetTags', tags)
                                    }
                                    savedTags={asset.assetTags}
                                />
                            </div>
                        )}

                        {showPopulationGroups && (
                            <div className={styles.formSection}>
                                <PopulationGroupMultiSelect
                                    selectedGroups={asset.assetGroups}
                                    onGroupsChange={groups =>
                                        updateAssetField('assetGroups', groups)
                                    }
                                    label={getText('msg_e744dd6b3c48')}
                                    showSelectedItemsInField={true}
                                    showSelectionBox={true}
                                />
                            </div>
                        )}

                        <div className={styles.formSection}>
                            <SwitchField
                                label={getText('msg_0c359a10ae17')}
                                onChange={isChecked =>
                                    updateAssetField(
                                        'assetTypeId',
                                        isChecked ? 1 : 2,
                                    )
                                }
                                tooltip={getText('msg_6bca40a374f0')}
                                checked={asset.assetTypeId === 1}
                            />
                        </div>
                    </div>

                    <div
                        className={`d-flex justify-content-between ${styles.assetFormFooter}`}
                    >
                        <button
                            type="button"
                            className="button-base ing-popup-blue-negative"
                            onClick={handleCancel}
                            disabled={isSaving}
                        >
                            {getText('msg_77dfd2135f4d')}
                        </button>
                        <button
                            type="button"
                            className="button-base ing-button-primary-left"
                            onClick={handleSubmit}
                            disabled={
                                mode === MODE.ADD ? !file || isSaving : isSaving
                            }
                        >
                            {isSaving && (
                                <Spinner
                                    width={20}
                                    height={20}
                                    className="me-2 d-inline-block"
                                />
                            )}
                            {isSaving
                                ? getText('msg_070e328ec8aa')
                                : submitLabel}
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};

export { AssetForm as default, MODE as ASSET_FORM_MODE };
